/**
 * The Discord section of the tasks dashboard: connected account, the links
 * table with status/toggles/whitelist/unlink, and the wizard entry point.
 */
import { useEffect, useState } from "react";
import { CircleAlert, CircleCheck, Plug, Plus, RefreshCw } from "lucide-react";
import { useDiscord } from "./useDiscordStore";
import { useDiscordLinkDialog } from "./DiscordLinkDialog";
import { Button } from "@/ui/Button";
import ds from "../tasks/dashboard.module.css";
import s from "./Discord.module.css";

/** Discord logo mark (lucide has none; single-path SVG, currentColor). */
function DiscordMark({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.32 4.37a19.8 19.8 0 0 0-4.89-1.52.07.07 0 0 0-.08.04c-.21.38-.44.87-.6 1.25a18.3 18.3 0 0 0-5.5 0 12.6 12.6 0 0 0-.61-1.25.08.08 0 0 0-.08-.04c-1.71.3-3.35.81-4.89 1.52a.07.07 0 0 0-.03.03C.53 9.05-.32 13.58.1 18.06c0 .02.01.04.03.05a19.9 19.9 0 0 0 6 3.03.08.08 0 0 0 .08-.03c.46-.63.87-1.3 1.23-2a.08.08 0 0 0-.04-.11 13.1 13.1 0 0 1-1.87-.9.08.08 0 0 1-.01-.12c.13-.1.25-.19.37-.29a.07.07 0 0 1 .08-.01c3.93 1.8 8.18 1.8 12.06 0a.07.07 0 0 1 .08 0c.12.1.25.2.37.3a.08.08 0 0 1 0 .12c-.6.35-1.22.64-1.88.9a.08.08 0 0 0-.04.1c.36.71.78 1.38 1.23 2a.08.08 0 0 0 .08.04 19.8 19.8 0 0 0 6.02-3.03.08.08 0 0 0 .03-.05c.5-5.18-.84-9.68-3.55-13.66a.06.06 0 0 0-.03-.03zM8.02 15.33c-1.18 0-2.16-1.08-2.16-2.42 0-1.33.96-2.42 2.16-2.42 1.21 0 2.18 1.1 2.16 2.42 0 1.34-.96 2.42-2.16 2.42zm7.97 0c-1.18 0-2.15-1.08-2.15-2.42 0-1.33.95-2.42 2.15-2.42 1.21 0 2.18 1.1 2.16 2.42 0 1.34-.95 2.42-2.16 2.42z" />
    </svg>
  );
}

export function DiscordSection() {
  const account = useDiscord((st) => st.account);
  const links = useDiscord((st) => st.links);
  const [confirmUnlink, setConfirmUnlink] = useState<string | null>(null);

  useEffect(() => {
    const st = useDiscord.getState();
    void st.refreshAccount();
    void st.refreshLinks();
  }, []);

  if (account && !account.configured) return null;

  return (
    <section className={ds.section}>
      <div className={ds.sectionHead}>
        <h2 className={ds.sectionTitle}>
          <span className={s.blurple}>
            <DiscordMark />
          </span>{" "}
          Discord
        </h2>
        {account?.connected ? (
          <Button variant="subtle" size="sm" onClick={() => useDiscordLinkDialog.getState().openWizard()}>
            <Plus size={13} /> Link a channel
          </Button>
        ) : null}
      </div>

      {!account ? (
        <p className={ds.mutedText}>Checking Discord connection...</p>
      ) : !account.connected ? (
        <div className={s.connectRow}>
          <p className={ds.mutedText}>
            Turn any Discord channel into a live task board: cards with buttons, synced both ways with your lists.
          </p>
          <Button variant="solid" className={s.connectBtn} onClick={() => useDiscordLinkDialog.getState().openWizard()}>
            <Plug size={13} /> Set up Discord
          </Button>
        </div>
      ) : (
        <>
          <div className={s.accountRow}>
            {account.account?.avatarUrl ? <img src={account.account.avatarUrl} alt="" className={s.accountAvatar} /> : null}
            <span className={s.accountName}>{account.account?.globalName ?? account.account?.username}</span>
            <span className={s.accountTag}>@{account.account?.username}</span>
            <Button variant="ghost" size="sm" onClick={() => void useDiscord.getState().disconnect()}>
              Disconnect
            </Button>
          </div>

          {links.length === 0 ? (
            <p className={ds.mutedText}>No channels linked yet.</p>
          ) : (
            <table className={ds.keyTable}>
              <thead>
                <tr>
                  <th>List</th>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Buttons</th>
                  <th>Whitelist</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {links.map((l) => (
                  <tr key={l.id}>
                    <td>{l.taskListName}</td>
                    <td>
                      <span className={s.channelCell}>
                        {l.guildName} <span className={s.channelHash}>#{l.channelName}</span>
                      </span>
                    </td>
                    <td>
                      {l.status === "ok" ? (
                        <span className={s.statusOk}>
                          <CircleCheck size={12} /> ok
                        </span>
                      ) : (
                        <span className={s.statusErr} title={l.statusReason ?? undefined}>
                          <CircleAlert size={12} /> {l.statusReason ?? "error"}
                          <Button variant="ghost" size="sm" onClick={() => void useDiscord.getState().verifyLink(l.id)}>
                            <RefreshCw size={11} /> Re-verify
                          </Button>
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={[s.toggle, l.buttonsEnabled ? s.toggleOn : ""].join(" ")}
                        aria-label={l.buttonsEnabled ? "Disable buttons" : "Enable buttons"}
                        onClick={() => void useDiscord.getState().patchLink(l.id, { buttonsEnabled: !l.buttonsEnabled })}
                      >
                        <span className={s.toggleKnob} />
                      </button>
                    </td>
                    <td>
                      <Button variant="ghost" size="sm" onClick={() => useDiscordLinkDialog.getState().openManage(l)}>
                        {l.whitelistCount || "Owner only"}
                      </Button>
                    </td>
                    <td>
                      {confirmUnlink === l.id ? (
                        <span className={ds.confirmGroup}>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setConfirmUnlink(null);
                              void useDiscord.getState().deleteLink(l.id);
                            }}
                          >
                            Unlink
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setConfirmUnlink(null)}>
                            Keep
                          </Button>
                        </span>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => setConfirmUnlink(l.id)}>
                          Unlink
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
